/**
 * Ministry Activity Service
 * Handles ministry activity management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MinistryActivityService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MinistryActivityService.DEFAULT_SERVICE_PATH,
            basePath: MinistryActivityService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all ministry activities with optional filters
     * @param isActive - Optional filter for active/inactive activities
     * @param ministryId - Optional filter by ministry ID
     * @returns Array of ministry activities
     */
    async getAll(isActive, ministryId) {
        const response = await this.getAllRaw(isActive, ministryId);
        return response.data ?? [];
    }
    /**
     * Get all ministry activities with optional filters (raw response)
     * @param isActive - Optional filter for active/inactive activities
     * @param ministryId - Optional filter by ministry ID
     * @returns Raw response with array of ministry activities
     */
    getAllRaw(isActive, ministryId) {
        const params = {};
        if (isActive !== undefined)
            params.isActive = isActive;
        if (ministryId !== undefined)
            params.ministryId = ministryId;
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get ministry activity by ID
     * @param id - Ministry activity ID
     * @returns Ministry activity details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ministry activity with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ministry activity by ID (raw response)
     * @param id - Ministry activity ID
     * @returns Raw response with ministry activity details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new ministry activity
     * @param data - Ministry activity creation data
     * @returns Created ministry activity
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create ministry activity');
        }
        return response.data;
    }
    /**
     * Create a new ministry activity (raw response)
     * @param data - Ministry activity creation data
     * @returns Raw response with created ministry activity
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing ministry activity
     * @param id - Ministry activity ID
     * @param data - Ministry activity update data
     * @returns Updated ministry activity
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update ministry activity with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing ministry activity (raw response)
     * @param id - Ministry activity ID
     * @param data - Ministry activity update data
     * @returns Raw response with updated ministry activity
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a ministry activity
     * @param id - Ministry activity ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a ministry activity (raw response)
     * @param id - Ministry activity ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate ministry activity cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate ministry activity cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
MinistryActivityService.DEFAULT_SERVICE_PATH = 'departments';
MinistryActivityService.BASE_PATH = 'api/ministry-activity';
//# sourceMappingURL=MinistryActivityService.js.map